//utils/snapUtils.ts
export interface SnapRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface StageSize {
    width: number;
    height: number;
}

export interface Guide {
    points: number[];
}

export interface SnapResult {
    x: number;
    y: number;
    guides: Guide[];
}

export interface SnapInput {
    movingRect: SnapRect;
    referenceRects: SnapRect[];
    stageSize: StageSize;
    snapThreshold: number;
}

export function calculateSnapping({
    movingRect,
    referenceRects,
    stageSize,
    snapThreshold,
}: SnapInput): SnapResult {
    const { x, y, width, height } = movingRect;
    const { width: stageWidth, height: stageHeight } = stageSize;

    let snappedX = x;
    let snappedY = y;
    const guides: Guide[] = [];

    const trySnap = (a: number, b: number) => Math.abs(a - b) < snapThreshold;

    const edges = {
        left: x,
        right: x + width,
        centerX: x + width / 2,
        top: y,
        bottom: y + height,
        centerY: y + height / 2,
    };

    const snapLinesX = [
        0,
        stageWidth,
        stageWidth / 2,
        stageWidth / 4,
        (3 * stageWidth) / 4,
        ...referenceRects.flatMap((r) => [r.x, r.x + r.width, r.x + r.width / 2]),
    ];

    const snapLinesY = [
        0,
        stageHeight,
        stageHeight / 2,
        ...referenceRects.flatMap((r) => [r.y, r.y + r.height, r.y + r.height / 2]),
    ];

    [edges.left, edges.right, edges.centerX].forEach((edgePos) => {
        snapLinesX.forEach((line) => {
            if (trySnap(edgePos, line)) {
                if (edgePos === edges.left) snappedX = line;
                if (edgePos === edges.right) snappedX = line - width;
                if (edgePos === edges.centerX) snappedX = line - width / 2;
                guides.push({ points: [line, 0, line, stageHeight] });
            }
        });
    });

    [edges.top, edges.bottom, edges.centerY].forEach((edgePos) => {
        snapLinesY.forEach((line) => {
            if (trySnap(edgePos, line)) {
                if (edgePos === edges.top) snappedY = line;
                if (edgePos === edges.bottom) snappedY = line - height;
                if (edgePos === edges.centerY) snappedY = line - height / 2;
                guides.push({ points: [0, line, stageWidth, line] });
            }
        });
    });

    snappedX = Math.max(0, Math.min(snappedX, stageWidth - width));
    snappedY = Math.max(0, Math.min(snappedY, stageHeight - height));
 
    return { x: snappedX, y: snappedY, guides };
}


export interface GroupSnapInput {
    groupBounds: SnapRect;
    stageSize: StageSize;
    snapThreshold: number;
}


export function calculateGroupSnapping({
    groupBounds,
    stageSize,
    snapThreshold,
}: GroupSnapInput): SnapResult {

    const guides: Guide[] = [];

    let snappedX = groupBounds.x;
    let snappedY = groupBounds.y;

    const centerX = groupBounds.x + groupBounds.width / 2;
    const centerY = groupBounds.y + groupBounds.height / 2;


    const verticalCenters = [
        stageSize.width / 4,
        stageSize.width / 2,
        (3 * stageSize.width) / 4,
    ];

    for (const line of verticalCenters) {
        if (Math.abs(centerX - line) < snapThreshold) {

            snappedX = line - groupBounds.width / 2;

            guides.push({
                points: [
                    line,
                    0,
                    line,
                    stageSize.height,
                ],
            });

            break;
        }
    }


    const horizontalCenter = stageSize.height / 2;

    if (Math.abs(centerY - horizontalCenter) < snapThreshold) {

        snappedY = horizontalCenter - groupBounds.height / 2;

        guides.push({
            points: [
                0,
                horizontalCenter,
                stageSize.width,
                horizontalCenter,
            ],
        });
    }


    return {
        x: snappedX,
        y: snappedY,
        guides,
    };
}